import styled, { css } from 'styled-components';
import Modal from '../Modal';
import {
  VideoClip,
  ShareableImagePreviewType,
  ShareableImageType,
} from '../../types.ts/story';
import LinkedInIcon from '../../svgs/LinkedInIcon';
import FacebookIcon from '../../svgs/FacebookIcon';
import XIcon from '../../svgs/XIcon';
import InstagramIcon from '../../svgs/InstagramIcon';
import TiktokIcon from '../../svgs/TiktokIcon';
import { Dispatch, ReactNode, SetStateAction, useState } from 'react';
import { observer } from 'mobx-react-lite';
import ClipPostItem from './ClipPostItem';
import YouTubeIcon from '../../svgs/YouTubeIcon';
import { useFlagsCombination } from '../../utility/useFlagsCombination';
import SocialProfileRefreshOverlay from '../common/SocialProfileRefreshOverlay';

import { runInAction } from 'mobx';
import { AspectRatio } from '../../types.ts/video';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { useUserIdentity } from '@src/stores-v2/StoreContext';

type Props = {
  onClose: () => void;
  clip?: VideoClip;
  shareable?: ShareableImagePreviewType;
  children: ReactNode;
  selectedDimension: string;
  hideShareButton: boolean;
  imageRef?: React.RefObject<HTMLImageElement>;
  shareableImageToggle?: Dispatch<SetStateAction<ShareableImageType | null>>;
  posterUrl?: string;
};

const ShareModal = observer((props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const { clip, selectedDimension, shareable } = props;
  const [expanded, setExpanded] = useState('LinkedIn');
  const { enableCaptionTemplatesTab } = useFlagsCombination(
    userIdentity.currentRole,
  );

  const onExpand = (name: string) => {
    setExpanded(name);
  };

  return (
    <Modal
      isOpen
      allowPropagation
      onClose={() => {
        if (videoCreator.showRefreshStoryForSocialProfile) {
          runInAction(
            () => (videoCreator.showRefreshStoryForSocialProfile = false),
          );
        } else {
          props.onClose();
        }
      }}
      paddingVertical="0"
      customWidth="min(80%, 1250px)"
      alwaysShowCancel={videoCreator.showRefreshStoryForSocialProfile}
    >
      <Main>
        {videoCreator.showRefreshStoryForSocialProfile && (
          <SocialProfileRefreshOverlay />
        )}

        <LeftPane>{props.children}</LeftPane>
        <RightPane>
          <PostContent>
            <ClipPostItem
              Icon={<LinkedInIcon fillColor="currentColor" />}
              dimension={selectedDimension}
              name="LinkedIn"
              clip={clip}
              shareable={shareable}
              showBorderTop={false}
              lowerLimit={70}
              upperLimit={100}
              expanded={expanded}
              onExpand={onExpand}
              hideShareButton={props.hideShareButton}
              imageRef={props.imageRef}
              shareableImageToggle={props.shareableImageToggle}
              posterUrl={props.posterUrl}
            />
            <ClipPostItem
              Icon={<FacebookIcon />}
              dimension={selectedDimension}
              name="Facebook"
              clip={clip}
              shareable={shareable}
              lowerLimit={40}
              upperLimit={80}
              expanded={expanded}
              onExpand={onExpand}
              hideShareButton={props.hideShareButton}
              imageRef={props.imageRef}
              shareableImageToggle={props.shareableImageToggle}
              posterUrl={props.posterUrl}
            />
            <ClipPostItem
              Icon={<InstagramIcon strokeColor="#F3E9D7" />}
              dimension={selectedDimension}
              name="Instagram"
              clip={clip}
              shareable={shareable}
              lowerLimit={40}
              upperLimit={80}
              expanded={expanded}
              onExpand={onExpand}
              hideShareButton={props.hideShareButton}
              imageRef={props.imageRef}
              shareableImageToggle={props.shareableImageToggle}
              posterUrl={props.posterUrl}
            />
            {clip && (
              <ClipPostItem
                Icon={<YouTubeIcon />}
                dimension={selectedDimension}
                name="YouTube"
                clip={clip}
                shareable={shareable}
                lowerLimit={100}
                upperLimit={140}
                expanded={expanded}
                onExpand={onExpand}
                hideShareButton={props.hideShareButton}
                imageRef={props.imageRef}
                shareableImageToggle={props.shareableImageToggle}
                posterUrl={props.posterUrl}
              />
            )}
            <ClipPostItem
              Icon={<XIcon />}
              dimension={selectedDimension}
              name="Twitter"
              clip={clip}
              shareable={shareable}
              showBorderBottom={!clip}
              lowerLimit={100}
              upperLimit={140}
              expanded={expanded}
              onExpand={onExpand}
              hideShareButton={props.hideShareButton}
              imageRef={props.imageRef}
              shareableImageToggle={props.shareableImageToggle}
              posterUrl={props.posterUrl}
            />
            {clip && (
              <ClipPostItem
                Icon={<TiktokIcon fillColor="#F3E9D7" />}
                dimension={selectedDimension}
                name="TikTok"
                clip={clip}
                shareable={shareable}
                showBorderBottom={true}
                lowerLimit={100}
                upperLimit={140}
                expanded={expanded}
                onExpand={onExpand}
                hideShareButton={props.hideShareButton}
                imageRef={props.imageRef}
                shareableImageToggle={props.shareableImageToggle}
                posterUrl={props.posterUrl}
              />
            )}
          </PostContent>
        </RightPane>
      </Main>
    </Modal>
  );
});

export default ShareModal;

const Main = styled.div`
  color: #f3e9d7;
  padding: 0 0 0 20px;
  text-align: left;
  display: grid;
  grid-template-columns: 56% 44%;
  grid-gap: 20px;
`;

const LeftPane = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin: 20px 0;
`;

const RightPane = styled.div`
  border-left: 1px solid #484848;
  padding: 20px;
  padding-right: 0;
  margin-left: 20px;
`;

const PostContent = styled.div`
  padding-right: 20px;
  overflow-y: scroll;
  overscroll-behavior: contain;
  max-height: 565px;
`;
